import React, { useState, useRef } from 'react';
import { Routes, Route, useNavigate, Link, Navigate } from 'react-router-dom';
import './App.css';
import api from './utils/api.js';
import { userFacingDetail } from './utils/errorHandler.js';
import { PASSWORD_RULES, isPasswordValid } from './utils/passwordValidation.js';
import { AuthProvider, useAuth } from './contexts/AuthContext.js';
import { JobUploadProvider } from './contexts/JobUploadContext.js';

// 💡 ページコンポーネントをインポート
import DashboardPage from './pages/DashboardPage';
import OpportunityListPage from './pages/OpportunityList';
import OpportunityDetailPage from './pages/OpportunityDetail';
import ActivityDetailPage from './pages/ActivityDetail';
import ProposedJobDetailPage from './pages/ProposedJobDetail';
import RecommendationListPage from './pages/Matching/RecommendationList';
import FileViewerPage from './pages/FileViewer';
import LoginPage from './pages/Login';
import UserListPage from './pages/UserList';
import UserArchivePage from './pages/UserList/UserArchivePage';
import PromptManagementPage from './pages/PromptManagement';
import PromptEditPage from './pages/PromptEdit';
import SettingsPage from './pages/Settings';

/**
 * AppContent - AuthContext を利用するメインコンテンツ
 */
function AppContent() {
  const { currentUser, isInitializing, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const menuRef = useRef(null);
  const navigate = useNavigate();

  // 💡 メニューの外側をクリックした時に閉じる処理
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 💡 パスワード変更処理
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!newPassword) {
      setPasswordError('新しいパスワードを入力してください。');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('パスワードが一致しません。');
      return;
    }
    if (!isPasswordValid(newPassword)) {
      setPasswordError('パスワードは8文字以上で、英字と数字をそれぞれ1文字以上含めてください。');
      return;
    }

    setLoadingPassword(true);
    try {
      const response = await api.fetch('/users/me/password', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: newPassword })
      });

      if (response && response.ok) {
        setPasswordSuccess('パスワードを変更しました。');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => setPasswordModalOpen(false), 2000);
      } else {
        const errData = await response.json().catch(() => null);
        setPasswordError(userFacingDetail(response.status, errData) || 'パスワードの変更に失敗しました。');
      }
    } catch (err) {
      setPasswordError('ネットワークエラーが発生しました。');
    } finally {
      setLoadingPassword(false);
    }
  };

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
  };

  // 💡 初期化中はスケルトン表示（他のコンポーネントのAPI呼び出しを防止）
  if (isInitializing) {
    return (
      <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8f9fb' }}>
        <div className="skeleton-container skeleton-fade-in" role="status" aria-label="読み込み中" style={{ width: '100%', maxWidth: '400px' }}>
          <div className="skeleton-line skeleton-title" />
          <div className="skeleton-line skeleton-subtitle" />
          <div className="skeleton-line" style={{ height: '16px', width: '80%', marginTop: '16px' }} />
          <div className="skeleton-line" style={{ height: '16px', width: '60%', marginTop: '12px' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="app-header">
        <div className="app-logo" style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
          <img src="/career-pilot.png" alt="キャリアパイロット" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <nav style={{ display: 'flex', gap: '20px' }}>
            <Link to="/" style={{ color: '#3a4a5c', textDecoration: 'none', fontWeight: '700' }}>マイページ</Link>
            <Link to="/opportunities" style={{ color: '#3a4a5c', textDecoration: 'none', fontWeight: '700' }}>案件一覧</Link>
          </nav>

          {currentUser && (
            <div className="user-profile-section" ref={menuRef}>
              <button
                className="user-icon-button"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                title={currentUser.user_name}
              >
                {currentUser.user_name.charAt(0).toUpperCase()}
              </button>

              {userMenuOpen && (
                <div className="user-dropdown">
                  <div className="dropdown-header">
                    <span className="dropdown-user-name">{currentUser.user_name}</span>
                    <span className="dropdown-user-email">{currentUser.user_email}</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  {currentUser.is_admin && (
                    <button className="dropdown-item" onClick={() => {
                      navigate('/settings');
                      setUserMenuOpen(false);
                    }}>
                      管理者設定
                    </button>
                  )}
                  <button className="dropdown-item" onClick={() => {
                    setPasswordModalOpen(true);
                    setUserMenuOpen(false);
                    setPasswordError('');
                    setPasswordSuccess('');
                  }}>
                    パスワード変更
                  </button>
                  <button className="dropdown-item" onClick={handleLogout}>
                    ログアウト
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="app-main">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* 認証ガード: ログインしていない場合は /login にリダイレクト */}
          {isInitializing ? (
            <Route path="*" element={<div className="loading-container"><div className="loading-spinner"></div></div>} />
          ) : !currentUser ? (
            <Route path="*" element={<Navigate to="/login" replace />} />
          ) : (
            <>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/opportunities" element={<OpportunityListPage />} />
              <Route path="/settings" element={<SettingsPage />}>
                <Route index element={<Navigate to="/settings/users" replace />} />
                <Route path="users" element={<UserListPage />} />
                <Route path="users/archive" element={<UserArchivePage />} />
                <Route path="prompts" element={<PromptManagementPage />} />
                <Route path="prompts/new" element={<PromptEditPage />} />
                <Route path="prompts/:templateId/edit" element={<PromptEditPage />} />
              </Route>
              <Route path="/opportunity/:opportunityId" element={<OpportunityDetailPage />} />
              <Route path="/opportunity/:opportunityId/matching" element={<RecommendationListPage />} />
              <Route path="/activity/:meetingId" element={<ActivityDetailPage />} />
              <Route path="/file-viewer" element={<FileViewerPage />} />
              <Route path="/proposed_job/:jobId" element={<ProposedJobDetailPage />} />
            </>
          )}
        </Routes>
      </main>

      {/* パスワード変更モーダル */}
      {passwordModalOpen && (
        <div className="modal-overlay" onClick={() => setPasswordModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">パスワード変更</h3>
            <form onSubmit={handlePasswordChange}>
              <div className="form-group">
                <label>新しいパスワード</label>
                <div className="password-toggle-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="auth-input"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    style={{ paddingRight: '50px' }}
                  />
                  <button
                    type="button"
                    className="password-toggle-button"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "隠す" : "表示"}
                  </button>
                </div>
                {newPassword && (
                  <div className="password-rules">
                    {PASSWORD_RULES.map((rule, i) => (
                      <small key={i} className={rule.test(newPassword) ? 'rule-pass' : 'rule-fail'}>
                        {rule.test(newPassword) ? '✓' : '✗'} {rule.label}
                      </small>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>新しいパスワード（確認）</label>
                <div className="password-toggle-container">
                  <input
                    type={showPasswordConfirm ? "text" : "password"}
                    className="auth-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={{ paddingRight: '50px' }}
                  />
                  <button
                    type="button"
                    className="password-toggle-button"
                    onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  >
                    {showPasswordConfirm ? "隠す" : "表示"}
                  </button>
                </div>
              </div>

              {passwordError && <p className="modal-error-message">{passwordError}</p>}
              {passwordSuccess && <p className="modal-success-message" style={{ color: '#28a745', textAlign: 'center', marginBottom: '15px' }}>{passwordSuccess}</p>}

              <div className="modal-actions">
                <button
                  type="button"
                  className="modal-button-cancel"
                  onClick={() => setPasswordModalOpen(false)}
                  disabled={loadingPassword}
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  className="modal-button-confirm"
                  disabled={loadingPassword || !isPasswordValid(newPassword)}
                >
                  {loadingPassword ? '変更中...' : '変更する'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      {/* 求人ファイルの取込状況を画面遷移をまたいで保持する (SCR-M-001 §6.2、Issue #759) */}
      <JobUploadProvider>
        <AppContent />
      </JobUploadProvider>
    </AuthProvider>
  );
}

export default App;